
import { useState } from "react";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Voucher } from "@/types/voucher";
import { toast } from "sonner";
import { useUserLocation } from "@/hooks/useUserLocation";
import { LocationPermissionDialog } from "./LocationPermissionDialog";
import { supabase } from "@/integrations/supabase/client";
import { useVenues } from "@/hooks/useVenues";
import { VoucherDetails } from "./VoucherDetails";
import { RedemptionSlider } from "./RedemptionSlider";

interface VoucherModalProps {
  voucher: Voucher;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VoucherModal({ 
  voucher, 
  open, 
  onOpenChange 
}: VoucherModalProps) {
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const { latitude, longitude, error, requestPermission } = useUserLocation();
  const { isUserAtVenue } = useVenues();

  const handleRedeemVoucher = async () => {
    // First check if we already have location permissions
    if (!latitude && !longitude && !error) {
      setShowLocationPrompt(true);
      return;
    }
    
    try {
      setIsRedeeming(true);
      
      // Check if user is at the venue
      if (latitude && longitude && voucher.venue.id) {
        // Get venue details to verify location
        const { data: venueData, error: venueError } = await supabase
          .from('venues')
          .select('latitude, longitude')
          .eq('id', voucher.venue.id)
          .single();
          
        if (venueError || !venueData) {
          throw new Error("Venue information not found");
        }
        
        if (!venueData.latitude || !venueData.longitude) {
          throw new Error("Venue location not available");
        }
        
        // Check if user is within 200 meters of the venue
        const isAtVenue = isUserAtVenue(
          { latitude, longitude },
          {
            id: voucher.venue.id,
            name: voucher.venue.name,
            address: voucher.venue.address || null,
            latitude: venueData.latitude,
            longitude: venueData.longitude
          },
          0.2 // 200 meters
        );
        
        if (!isAtVenue) {
          throw new Error("You must be at the venue to redeem this voucher");
        }
      } else {
        // For testing, we'll skip location check if venue has no coordinates
        console.log("Skipping location check due to missing coordinates");
      }
      
      // Update voucher status to redeemed in Supabase
      const { error: updateError } = await supabase
        .from('vouchers')
        .update({
          status: 'redeemed',
          redeemed_at: new Date().toISOString(),
          redeemed_location: voucher.venue.id
        })
        .eq('id', voucher.id);
        
      if (updateError) {
        throw updateError;
      }
      
      toast.success("Voucher redeemed successfully!");
      onOpenChange(false);
    } catch (error) {
      console.error("Error redeeming voucher:", error);
      toast.error(error instanceof Error ? error.message : "Failed to redeem voucher");
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleLocationPermission = async (granted: boolean) => {
    setShowLocationPrompt(false);
    
    if (granted) {
      await requestPermission();
    } else {
      toast.error("Location permission is required to redeem vouchers");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-black border-gray-800 text-[#FFFCF3] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{voucher.title}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {voucher.sponsorName}
            </DialogDescription>
          </DialogHeader>
          
          <VoucherDetails voucher={voucher} />
          
          <RedemptionSlider 
            voucher={voucher} 
            isRedeeming={isRedeeming} 
            onRedeem={handleRedeemVoucher} 
          />
        </DialogContent>
      </Dialog>
      
      <LocationPermissionDialog 
        open={showLocationPrompt}
        onOpenChange={setShowLocationPrompt}
        onConfirm={() => handleLocationPermission(true)}
        onCancel={() => handleLocationPermission(false)}
      />
    </>
  );
}
